import { Button, Form, message } from "antd";
import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import * as XLSX from "xlsx";
import UploadExcelIcon from "../../../../../../assets/icons/UploadExcelIcon";
import CustomModal from "../../../../../../components/ui/CustomModal";
import { useGiveKPIAchievementMutation } from "../../../../../../features/organization/contractual/KPISetup/kpiSetupApi";
import useError from "../../../../../../hooks/useError";
import { toast } from "../../../../../../lib/toast";
import { CustomModalProps } from "../../../../../../types";

type ExcelRow = Record<string, any>;

function transformExcelData(
  data: ExcelRow[],
  kpiGroupId?: number,
  cycle?: string
) {
  if (!data || data.length === 0) return null;

  const firstRow = data[0];

  const kpiColumns = Object.keys(firstRow).filter((key) =>
    key.startsWith("KPI_")
  );

  const periodFrom = dayjs(firstRow["Period From"], "YYYY-MM-DD");
  const periodTo = dayjs(firstRow["Period To"], "YYYY-MM-DD");

  const transformed = {
    cycle: `${periodFrom.format("D MMM, YYYY")} – ${periodTo.format(
      "D MMM, YYYY"
    )}`,
    payment_type: firstRow["Payment Type"] || "",
    period_from: periodFrom.format("YYYY-MM-DD"),
    period_to: periodTo.format("YYYY-MM-DD"),
    kpi_group_id: kpiGroupId || firstRow["KPI Group Id"],
    rows: data.map((row, rowIndex) => {
      const achievements: Record<string, any> = {};

      kpiColumns.forEach((col) => {
        const match = col.match(/^KPI_(\d+)/);
        if (match) {
          const kpiNumber = match[1];
          const value = Number(row[col] || 0);

          // 🔥 VALIDATION: KPI value cannot exceed 100
          if (value > 100) {
            toast(
              "error",
              `Row ${
                rowIndex + 1
              }: KPI ${kpiNumber} value (${value}) cannot exceed 100`
            );
          }

          achievements[kpiNumber] = value;
        }
      });

      return {
        assignment_id: row["Assignment Id"],
        job_apply_id: row["Job Apply Id"],
        member_id: row["Employee Id"],
        employee_id: `EMP-${row["Employee Id"]}`,
        employee_name: row["Employee Name"],
        kpi_group_id: row["KPI Group Id"],
        achievements,
      };
    }),
  };

  return transformed;
}

const ImportKPIPerformance = ({ open, handleClose }: CustomModalProps) => {
  const [form] = Form.useForm();
  const [file, setFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [giveReport, { isLoading, isSuccess, isError, error }] =
    useGiveKPIAchievementMutation();

  // ---- Error Handaling -----
  useError(isError, error, "Failed KPI performance imported");

  const onFinish = () => {
    if (!file) {
      message.error("Please select an Excel file!");
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const data = new Uint8Array(e.target?.result as ArrayBuffer);
      const workbook = XLSX.read(data, { type: "array" });

      // Assuming first sheet
      const firstSheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[firstSheetName];

      // Convert to JSON
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: "" });
      const formattedData = transformExcelData(
        jsonData as unknown as ExcelRow[]
      );

      giveReport(formattedData);

      // console.log("formattedData:", formattedData);
    };

    reader.readAsArrayBuffer(file);
  };

  useEffect(() => {
    if (isSuccess) {
      handleClose && handleClose();
      toast("success", "KPI performance imported successfully.");
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile && /\.(xlsx|xls)$/i.test(selectedFile.name)) {
      setFile(selectedFile);
    } else {
      message.error("Only Excel files are allowed!");
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const droppedFile = e.dataTransfer.files?.[0];
    if (droppedFile && /\.(xlsx|xls)$/i.test(droppedFile.name)) {
      setFile(droppedFile);
    } else {
      message.error("Only Excel files are allowed!");
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <CustomModal
      footer={null}
      open={open}
      handleClose={() => {
        handleClose && handleClose();
        form.resetFields();
        setFile(null);
      }}
      title="Import KPI Performance"
    >
      <Form
        className="custom-form"
        layout="vertical"
        requiredMark="optional"
        size="large"
        onFinish={onFinish}
        form={form}
      >
        <Form.Item
          name="file"
          label="Select Job"
          className="mb-4"
          rules={[{ required: true, message: "Please upload an Excel file!" }]}
        >
          <label
            id="excel"
            className={`flex flex-col items-center gap-6 w-full min-h-[200px] border border-extra-light border-dashed rounded-lg p-5 cursor-pointer ${
              file ? "border-primary bg-primary-light/10" : ""
            }`}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onClick={() => fileInputRef.current?.click()}
          >
            <UploadExcelIcon />
            <div className="flex flex-col items-center text-center gap-2.5">
              {file ? (
                <p className="font-medium">{file.name}</p>
              ) : (
                <>
                  <p>Drag and drop your Excel file (.xlsx, .xls) here.</p>
                  <p>Or</p>
                  <Button className="import-button h-10" size="middle">
                    Select File
                  </Button>
                </>
              )}
            </div>
            <input
              ref={fileInputRef}
              name="excel"
              className="!hidden"
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileChange}
            />
          </label>
        </Form.Item>

        <div className="flex items-center gap-4">
          <Button
            size="middle"
            className="h-10 min-w-[125px] normal-dark-button"
            onClick={() => {
              handleClose && handleClose();
              form.resetFields();
              setFile(null);
            }}
          >
            Cancel
          </Button>
          <Button
            size="middle"
            htmlType="submit"
            className="h-10 min-w-[125px]"
            type="primary"
            loading={isLoading}
          >
            Import
          </Button>
        </div>
      </Form>
    </CustomModal>
  );
};

export default ImportKPIPerformance;
